import { Avatar, Empty, ErrorState, Loading, PageHead, StatusBadge, useFormat } from '@ly360/ui';
import { useQuery } from '@tanstack/react-query';
import { Building2, Handshake, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import type { AccountRow } from '../crm/data';
import { NewCaseDialog, NewCompanyDialog, NewDealDialog, NewPersonDialog } from '../crm/forms';
import { Notes, Timeline } from '../crm/Timeline';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

export function CompaniesPage() {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const list = useQuery({
    queryKey: ['accounts', q],
    queryFn: () => api<{ items: AccountRow[] }>(`/v1/accounts${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  });
  const ar = i18n.language === 'ar';
  return (
    <>
      <PageHead
        eyebrow={t('nav.main')}
        title={t('crm.companies.title')}
        subtitle={t('crm.companies.subtitle')}
        actions={
          can('accounts:write') && (
            <button className="btn btn-grad" onClick={() => setOpen(true)} data-testid="add-company">
              <Building2 size={16} />
              {t('crm.companies.new')}
            </button>
          )
        }
      />
      <section className="card">
        <div className="toolbar">
          <input
            className="input"
            type="search"
            placeholder={t('crm.companies.search')}
            aria-label={t('common.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {list.isLoading && <Loading />}
        {list.isError && <ErrorState error={list.error} />}
        {list.data?.items.length === 0 && <Empty text={t('crm.companies.empty')} />}
        {!!list.data?.items.length && (
          <div className="table-wrap">
            <table className="table table-responsive">
              <thead>
                <tr>
                  <th>{t('crm.companies.col.name')}</th>
                  <th>{t('crm.companies.col.industry')}</th>
                  <th>{t('crm.companies.col.city')}</th>
                  <th>{t('crm.companies.col.people')}</th>
                  <th>{t('crm.companies.col.openDeals')}</th>
                  <th>{t('crm.companies.col.openCases')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((a) => (
                  <tr key={a.id} onClick={() => navigate(`/companies/${a.id}`)} style={{ cursor: 'pointer' }}>
                    <td data-label={t('crm.companies.col.name')}>
                      <Link to={`/companies/${a.id}`} onClick={(e) => e.stopPropagation()}>
                        <strong dir="auto">{(ar && a.nameAr) || a.name}</strong>
                      </Link>
                    </td>
                    <td data-label={t('crm.companies.col.industry')}>{a.industry ?? '—'}</td>
                    <td data-label={t('crm.companies.col.city')}>{a.city ?? '—'}</td>
                    <td data-label={t('crm.companies.col.people')} className="num">
                      {a.peopleCount}
                    </td>
                    <td data-label={t('crm.companies.col.openDeals')} className="num">
                      {a.openDeals}
                    </td>
                    <td data-label={t('crm.companies.col.openCases')} className="num">
                      {a.openCases}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {open && <NewCompanyDialog open onClose={() => setOpen(false)} />}
    </>
  );
}

interface CompanyDetail {
  account: AccountRow;
  people: { id: string; displayName: string; jobTitle: string | null; role: string | null }[];
  deals: {
    id: string;
    title: string;
    amountMinor: string;
    currency: string;
    status: string;
    stageEn: string;
    stageAr: string;
  }[];
  cases: { id: string; number: string; subject: string; status: string }[];
}

export function CompanyPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const fmt = useFormat();
  const [dialog, setDialog] = useState<null | 'person' | 'deal' | 'case'>(null);
  const q = useQuery({ queryKey: ['account', id], queryFn: () => api<CompanyDetail>(`/v1/accounts/${id}`) });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState error={q.error} />;
  const { account: a, people, deals, cases } = q.data;
  const ar = i18n.language === 'ar';
  const close = () => setDialog(null);
  return (
    <>
      <div className="record-head">
        <Avatar name={a.name} size={56} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 dir="auto">{(ar && a.nameAr) || a.name}</h1>
          <div className="sub">{[a.industry, a.city, a.website].filter(Boolean).join(' · ')}</div>
        </div>
      </div>
      <div className="quick-actions" style={{ marginBottom: '1rem' }}>
        {can('persons:write') && (
          <button className="btn btn-ghost btn-sm" onClick={() => setDialog('person')}>
            <UserPlus size={14} />
            {t('crm.companies.addPerson')}
          </button>
        )}
        {can('deals:write') && (
          <button className="btn btn-ghost btn-sm" onClick={() => setDialog('deal')}>
            <Handshake size={14} />
            {t('crm.person.newDeal')}
          </button>
        )}
        {can('cases:write') && (
          <button className="btn btn-ghost btn-sm" onClick={() => setDialog('case')}>
            {t('crm.person.newCase')}
          </button>
        )}
      </div>
      <div className="record-grid">
        <section className="card">
          <div className="card-head">
            <h2>{t('crm.person.timeline')}</h2>
          </div>
          <Timeline path={`/v1/accounts/${id}/timeline`} />
        </section>
        <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
          <section className="card">
            <div className="card-head">
              <h2>{t('crm.companies.people')}</h2>
            </div>
            <ul className="list-plain">
              {people.map((p) => (
                <li key={p.id}>
                  <Link to={`/people/${p.id}`} className="grow" dir="auto">
                    <strong>{p.displayName}</strong>
                    <div className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                      {p.role ?? p.jobTitle ?? ''}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
          {deals.length > 0 && (
            <section className="card">
              <div className="card-head">
                <h2>{t('crm.person.deals')}</h2>
              </div>
              <ul className="list-plain">
                {deals.map((d) => (
                  <li key={d.id}>
                    <div className="grow">
                      <div dir="auto">{d.title}</div>
                      <div className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                        {ar ? d.stageAr : d.stageEn}
                      </div>
                    </div>
                    <strong>{fmt.money(Number(d.amountMinor), d.currency)}</strong>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {cases.length > 0 && (
            <section className="card">
              <div className="card-head">
                <h2>{t('crm.person.cases')}</h2>
              </div>
              <ul className="list-plain">
                {cases.map((c) => (
                  <li key={c.id}>
                    <Link to={`/cases/${c.id}`} className="grow">
                      #{c.number} {c.subject}
                    </Link>
                    <StatusBadge value={c.status} ns="crm.caseStatus" />
                  </li>
                ))}
              </ul>
            </section>
          )}
          <Notes relatedType="account" relatedId={id} />
        </div>
      </div>
      {dialog === 'person' && <NewPersonDialog open onClose={close} accountId={id} />}
      {dialog === 'deal' && <NewDealDialog open onClose={close} accountId={id} />}
      {dialog === 'case' && <NewCaseDialog open onClose={close} accountId={id} />}
    </>
  );
}
